import React from "react";
import { focusRing } from "../../pages/Exams/TestBuilder/ui";

// badge: a value in a list; dot: its filter chip.
export const CHIP_TONES = {
  slate: { badge: "bg-slate-100 text-slate-700 ring-slate-500/20", dot: "bg-slate-400" },
  amber: { badge: "bg-amber-50 text-amber-800 ring-amber-600/20", dot: "bg-amber-500" },
  blue: { badge: "bg-blue-50 text-blue-700 ring-blue-600/20", dot: "bg-blue-500" },
  violet: { badge: "bg-violet-50 text-violet-700 ring-violet-600/20", dot: "bg-violet-500" },
  cyan: { badge: "bg-cyan-50 text-cyan-800 ring-cyan-600/20", dot: "bg-cyan-500" },
  green: { badge: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", dot: "bg-emerald-500" },
  red: { badge: "bg-red-50 text-red-700 ring-red-600/20", dot: "bg-red-500" },
};

// White whatever its tone (the tone is only the dot), so the row stays calm and the selected chip is its one dark
// mark. A chip with nothing behind it (count 0) steps back: no fill, a grey dot. 44px tall on phones (a touch target).
function Chip({ selected, onClick, label, count, tone }) {
  const empty = count === 0;
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`inline-flex h-11 shrink-0 items-center sm:h-9 gap-2 whitespace-nowrap rounded-full px-3.5 text-sm ring-1 ring-inset transition duration-200 ${focusRing} ${
        selected
          ? "bg-slate-900 font-semibold text-white ring-slate-900"
          : empty
          ? "font-normal text-slate-500 ring-slate-200 hover:bg-white hover:text-slate-900"
          : "bg-white font-medium text-slate-700 ring-slate-200 hover:bg-slate-50 hover:text-slate-900 hover:ring-slate-300"
      }`}
    >
      {tone && <span className={`h-2 w-2 shrink-0 rounded-full ${empty && !selected ? "bg-slate-300" : tone.dot}`} aria-hidden="true" />}
      {label}
      {count !== undefined && count !== null && (
        <span className={`text-xs font-semibold tabular-nums ${selected ? "text-white/70" : "text-slate-500"}`}>{count}</span>
      )}
    </button>
  );
}

/**
 * A filter as a row of chips: "All" (`allLabel`, `allCount`) and one chip per item ({value, label, count,
 * tone} with a tone from CHIP_TONES), `selected` being an item's value or "all". A click on the selected chip
 * goes back to "all". `label` names the group for screen readers. Phones scroll the row sideways.
 */
export default function FilterChips({ label, allLabel, allCount, items, selected, onSelect }) {
  return (
    <div
      role="group"
      aria-label={label}
      className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0"
    >
      <Chip selected={selected === "all"} onClick={() => onSelect("all")} label={allLabel} count={allCount} />
      {items.map((item) => (
        <Chip
          key={item.value}
          selected={selected === item.value}
          onClick={() => onSelect(selected === item.value ? "all" : item.value)}
          label={item.label}
          count={item.count}
          tone={item.tone}
        />
      ))}
    </div>
  );
}
